import React, {useMemo, useRef, useState} from "react"
import {useSelector} from "react-redux"
import PhoneInput from "react-phone-input-2"
import es from "react-phone-input-2/lang/es.json"
import "react-phone-input-2/lib/material.css"
import "./input/style.css"
import "./phoneField.css"
import {PHONE_COUNTRIES} from "../constants/phonePrefixes"
import {RootState} from "../globals"

// Plantel de Torreón (tb_campuses.id): ahí la clave lada es de tres dígitos.
const PLANTEL_TORREON = 2

// Teléfono con código de país (bandera + buscador de país + número).
//
// Por qué existe (2026-09-11): los teléfonos se guardaban como los tecleaba la
// cajera ("94209714", "14238318203", "+17027215769") y los envíos de WhatsApp
// asumían que todo número de un plantel hondureño era hondureño. Los alumnos
// con número de EE. UU. o México nunca recibieron sus comprobantes; les
// llegaron a desconocidos. Con este campo el número se guarda SIEMPRE como
// dígitos con código de país (50494209714, 14238318203): el backend lo sigue
// aceptando como entero y cualquier sistema sabe a qué país marcar.
//
// Orden del selector: 1) país del plantel, 2) Estados Unidos, 3) resto (con
// buscador). Usa react-phone-input-2, que ya estaba en el proyecto con el CSS
// de subrayado del formulario (src/components/input/style.css).

const soloDigitos = (v: any) => String(v ?? "").replace(/\D/g, "")

// ct_countries.id -> ISO2 de los países donde hay planteles.
const ISO2_POR_PAIS: {[id: number]: string} = {1: "mx", 2: "hn", 60: "sv", 66: "us", 79: "gt", 37: "ca"}

export function prefixForCountryId(countryId?: number): string
{
	return PHONE_COUNTRIES.find(c => c.id === countryId)?.prefix || "504"
}

// Códigos de país que de verdad aparecen en el registro: los planteles, Estados
// Unidos y Canadá, el resto de Centroamérica y los países donde hay alumnos que
// se fueron. Se limita a propósito (Esteban, 2026-09-11): con la lista completa,
// un número mexicano mal capturado de 11 dígitos como 81211243024 "empezaba con
// 81" y se mostraba como japonés. Lo que no cae en esta lista se muestra tal
// como está guardado, que es la señal de que hay que corregirlo.
const PREFIJOS_CONOCIDOS = [
	"1", "52", "503", "504", "502", "505", "506", "507",
	"34", "39", "33", "44", "49", "51", "54", "55", "56", "57", "58", "591", "593"
]

// Un número al que se le pegó el código de país más de una vez ("52" "52" "52" y luego el
// número) se muestra con un solo código. Solo se quita mientras quede un número completo
// detrás, para no mutilar uno bueno (Esteban, 2026-09-12).
function sinCodigoRepetido(local: string, prefix: string): string
{
	let limpio = local

	while(limpio.startsWith(prefix) && limpio.length - prefix.length >= 9)
	{
		limpio = limpio.slice(prefix.length)
	}

	return limpio
}

// Separa "50494209714" en {prefix:"504", local:"94209714"}. Un número sin
// código (8 dígitos en HN, 10 en MX) se atribuye al país por defecto.
export function splitPhone(value: any, defaultPrefix: string): {prefix: string, local: string}
{
	const d = soloDigitos(value)
	if(!d) return {prefix: defaultPrefix, local: ""}
	const esLocal = d.length <= 10 && !(d.startsWith(defaultPrefix) && d.length - defaultPrefix.length >= 7)
	if(esLocal) return {prefix: defaultPrefix, local: d}
	//El país del plantel manda: si el número empieza con su código, es de aquí.
	if(d.startsWith(defaultPrefix) && d.length - defaultPrefix.length >= 7)
	{
		return {prefix: defaultPrefix, local: sinCodigoRepetido(d.slice(defaultPrefix.length), defaultPrefix)}
	}
	const prefixes = Array.from(new Set(PREFIJOS_CONOCIDOS)).sort((a, b) => b.length - a.length)
	for(const p of prefixes)
	{
		const rest = d.length - p.length
		if(d.startsWith(p) && rest >= 7 && rest <= 12) return {prefix: p, local: sinCodigoRepetido(d.slice(p.length), p)}
	}
	return {prefix: defaultPrefix, local: d}
}

// Valor completo con código, para que la librería reconozca el país aunque el
// registro viejo traiga sólo el número local.
export function normalizePhone(value: any, defaultPrefix: string): string
{
	const {prefix, local} = splitPhone(value, defaultPrefix)
	return local ? `${prefix}${local}` : ""
}

// Agrupa el número como lo escribe la gente de cada país: con guion los de ocho
// dígitos (9995-2923 en Honduras, El Salvador y Guatemala) y con espacio los demás
// (81 1234 5678 en México, 865 385 2163 en EE. UU.).
function agruparNumero(prefix: string, local: string): string
{
	if(prefix === "52" && local.length === 11 && local.startsWith("1"))
	{
		//México se guarda en formato WhatsApp (52 + 1 + 10 dígitos); el 1 no se muestra.
		local = local.slice(1)
	}

	if(local.length === 8)
	{
		return local.replace(/(\d{4})(\d{4})/, "$1-$2")
	}

	if(local.length === 9)
	{
		return local.replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3")
	}

	if(local.length === 10)
	{
		if(prefix === "52")
		{
			//En México la clave es de dos dígitos solo en Ciudad de México, Guadalajara y
			//Monterrey; en el resto, Torreón incluido, es de tres: 871 123 4567 (Esteban,
			//2026-09-11).
			return /^(55|56|33|81)/.test(local)
				? local.replace(/(\d{2})(\d{4})(\d{4})/, "$1 $2 $3")
				: local.replace(/(\d{3})(\d{3})(\d{4})/, "$1 $2 $3")
		}

		return local.replace(/(\d{3})(\d{3})(\d{4})/, "$1 $2 $3")
	}

	return local
}

// Cómo se ve un teléfono en pantalla (Esteban, 2026-09-11): el número del país del
// plantel se muestra como lo escribe la gente de ahí, sin el código; sólo los de otro
// país llevan su prefijo adelante, que es justo lo que hace falta saber de ellos.
export function formatPhoneDisplay(value: any, defaultPrefix: string): string
{
	const {prefix, local} = splitPhone(value, defaultPrefix)

	if(!local)
	{
		return ""
	}

	const agrupado = agruparNumero(prefix, local)

	return prefix === defaultPrefix ? agrupado : `+${prefix} ${agrupado}`
}

interface Props
{
	id              : string
	name            : string
	value           : any
	onChange        : (value: string) => void
	campusCountryId?: number
	disabled?       : boolean
	required?       : boolean
	/** Enter dentro del campo (la librería se lo come y el formulario no se envía). */
	onEnter?        : () => void
}

/*
	Un número de México guardado como 521 + 10 (formato WhatsApp) tiene un dígito más de lo que cabe en la máscara de la
	librería, que entonces lo mostraba cortado ("+52 18 1149 8736") y, si alguien tocaba el campo, lo guardaba cortado.
	A la librería siempre se le da 52 + 10; al guardar, México va SIEMPRE como 521 + 10 en todas las pantallas que usan
	este campo (Esteban, 2026-10-21: igual que WhatsApp y Woztell).
*/
const sinUnoDeMexico = (d: string): string => d.length === 13 && d.startsWith("521") ? "52" + d.slice(3) : d

const PhoneField = (props: Props) =>
{
	const iso2          = ISO2_POR_PAIS[props.campusCountryId || 0] || "hn"
	const defaultPrefix = prefixForCountryId(props.campusCountryId)
	// México y Honduras siempre arriba (alumnos de un país con el teléfono o la compu del otro; QA 2026-10-21).
	const preferred     = useMemo(() => Array.from(new Set([iso2, "mx", "hn", "us"])), [iso2])
	const paisActual    = useRef<string>(iso2)
	//La lada que se ve: la del número guardado (puede ser de otro país) o la del plantel; luego la que elija la persona.
	const ladaActual    = useRef<string>(splitPhone(String(props.value ?? ""), defaultPrefix).prefix || defaultPrefix)
	const [reinicio, setReinicio] = useState<number>(0)
	const enfocar       = () => setTimeout(() => { const el = document.getElementById(props.id) as HTMLInputElement | null; if(el) { el.focus(); const n = el.value.length; try { el.setSelectionRange(n, n) } catch(_) { /* tel sin selección */ } } }, 0)
	const emitir        = (salida: string) => { ultimoEmitido.current = salida; props.onChange(salida) }
	// Mientras la persona escribe, la librería ya entrega el número CON código
	// (+52 → "525..."); si lo volviéramos a normalizar en cada render, el código
	// se antepondría una y otra vez ("52 52 52 5", bug del 2026-09-11). Sólo se
	// normaliza el valor que llega de afuera (registro guardado, campo vacío);
	// lo que nosotros mismos emitimos se pasa tal cual.
	/*
		Al teclear, el número de México se agrupa como se muestra en Estudiantes (Esteban,
		2026-09-23): Monterrey "81 1234 5678", Torreón "871 123 4567". La librería agrupaba
		todos igual ("811 234 5678"). Su máscara es fija por país y se lee una sola vez, así
		que se escoge por el plantel en uso; el número que se guarda no cambia.
	*/
	const currentUserCampus: any = useSelector((state: RootState) => state.currentUserCampus.value)
	const campusRef: any = useSelector((state: RootState) => state.campusRef.value)
	const plantelId: number = Number(campusRef?.[currentUserCampus]?.id || 0)
	const mascaraMx: string = plantelId === PLANTEL_TORREON ? "... ... ...." : ".. .... ...."
	const ultimoEmitido = useRef<string | null>(null)
	/*
		Pegar el número como lo da WhatsApp ("5218100000102", "+52 1 81…", "528100000102") o los 10/8 dígitos solos: la
		librería lo dejaba vacío o le metía el "1" a la máscara y salía otro número (QA 2026-10-21). Se resuelve aquí.
	*/
	const alPegar = (e: React.ClipboardEvent<HTMLInputElement>) =>
	{
		const d = soloDigitos(e.clipboardData?.getData("text"))
		let salida = ""
		if(d.length === 13 && d.startsWith("521")) salida = d
		else if(d.length === 12 && d.startsWith("52")) salida = "521" + d.slice(2)
		else if(d.length === 11 && d.startsWith("504")) salida = d
		else if(d.length === 10 && paisActual.current === "mx") salida = "521" + d
		else if(d.length === 8 && paisActual.current === "hn") salida = "504" + d
		if(salida === "") return
		e.preventDefault()
		paisActual.current = salida.startsWith("504") ? "hn" : "mx"
		//La lada cambia con lo pegado: si no, al soltar Ctrl+V el campo creía que se había borrado el código y lo vaciaba.
		ladaActual.current = salida.startsWith("504") ? "504" : "52"
		emitir(salida)
		enfocar()
	}
	/*
		Si borran también el "+52", la librería (código no editable) ya no acepta ninguna tecla: el campo se quedaba trabado
		(QA 2026-10-21). En cuanto lo escrito ya no empieza con la lada, se rearma el campo con "+52" y el cursor al final.
	*/
	const alSoltarTecla = (e: React.KeyboardEvent<HTMLInputElement>) =>
	{
		const v = (e.currentTarget.value || "").replace(/\s/g, "")
		//La lada se toma de lo que el campo tiene AHORA (pegado, país elegido o guardado), no de un valor viejo.
		const actual = soloDigitos(props.value)
		const ladaVista = actual ? splitPhone(actual, defaultPrefix).prefix : ladaActual.current
		if(!v.startsWith("+" + ladaVista) && !v.startsWith("+" + ladaActual.current))
		{
			emitir("")
			setReinicio((n) => n + 1)
			enfocar()
		}
	}
	const entrante      = String(props.value ?? "")
	const value         = sinUnoDeMexico(ultimoEmitido.current !== null && entrante === ultimoEmitido.current
		? entrante
		: normalizePhone(entrante, defaultPrefix))

	return <PhoneInput
	  containerClass="phoneField"
	  country={iso2}
	  preferredCountries={preferred}
	  key={"mx_" + mascaraMx + "_" + reinicio}
	  masks={{hn: ".... ....", sv: ".... ....", gt: ".... ....", mx: mascaraMx}}   // HN/SV/GT se leen de 4 en 4 (Esteban, 2026-09-11)
	  preserveOrder={["preferredCountries"]}
	  value={value}
	  localization={es as any}
	  enableSearch
	  searchPlaceholder="Buscar país"
	  searchNotFound="Sin resultados"
	  countryCodeEditable={false}
	  enableLongNumbers={true}   // si escriben rápido "+52 1 81…", el 1 sobra un instante: que no se coma el último dígito
	  disabled={props.disabled}
	  specialLabel=""
	  placeholder=""
	  inputProps={{id: props.id, name: props.name, required: props.required, minLength: 8, onPaste: alPegar, onKeyUp: alSoltarTecla}}
	  onEnterKeyPress={props.onEnter ? () => props.onEnter && props.onEnter() : undefined}
	  onChange={(v: string, country: any) =>
	  {
		  const pais = String(country?.countryCode || country?.iso2 || "")
		  let d = soloDigitos(v)
		  // Borró hasta el "+52": la librería ya no aceptaba teclas (código no editable). Se rearma con el código del país.
		  if(d === "")
		  {
			  emitir("")
			  setReinicio((n) => n + 1)
			  enfocar()
			  return
		  }
		  // Eligió otro país: el cursor vuelve al número (antes lo tecleado después se perdía).
		  if(country?.dialCode) ladaActual.current = String(country.dialCode)
		  if(pais && pais !== paisActual.current)
		  {
			  paisActual.current = pais
			  enfocar()
		  }
		  // México: el "1" de celular que muestra WhatsApp (+52 1 81…) no va en la máscara; ningún número de México empieza con 1.
		  if(pais === "mx" && d.startsWith("521"))
		  {
			  d = "52" + d.slice(3)
		  }
		  // Sólo el código de país (el usuario cambió de país sin escribir número) = vacío.
		  let salida = d && d !== soloDigitos(country?.dialCode) ? d : ""
		  if(pais === "mx" && salida.length === 12 && salida.startsWith("52"))
		  {
			  salida = "521" + salida.slice(2)
		  }
		  emitir(salida)
	  }}
	/>
}

export default PhoneField
